// LLM generated - Model Claude 4 Sonnet
import { forwardRef, cloneElement, isValidElement } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Stack, Box, useMantineTheme } from '@mantine/core';
import type { Sx, MantineTheme } from '@mantine/core';

import dt from '~/testing';
import type { TextProps } from '~/domains/design-system/Text';
import { Text } from '~/domains/design-system/Text';

// ========================================
// Types and Interfaces
// ========================================

type EmptyStateSize = 'xs' | 'sm' | 'md' | 'lg';

const EMPTY_STATE_SIZES = {
  xs: { iconSize: 24, spacing: 'xs' as const, iconMarginBottom: 8 },
  sm: { iconSize: 32, spacing: 'sm' as const, iconMarginBottom: 16 },
  md: { iconSize: 48, spacing: 'md' as const, iconMarginBottom: 24 },
  lg: { iconSize: 64, spacing: 'lg' as const, iconMarginBottom: 32 },
} as const;

// Base props that extend Mantine Stack - inherits all Stack props including c, sx, className
type EmptyStateBaseProps = ComponentPropsWithoutRef<typeof Stack>;

// Direct props pattern (new capability)
interface EmptyStateDirectProps extends EmptyStateBaseProps {
  /** Background color for the empty state */
  backgroundColor?: string;
  /** Border radius for the empty state */
  borderRadius?: number;
  /** Custom icon component (optional) */
  icon?: ReactNode;
  /** Main heading text */
  title?: string;
  /** Description text with optional inline elements */
  description?: ReactNode;
  /** Size variant for icon and spacing */
  size?: EmptyStateSize;
  /** Show border around the empty state */
  showBorder?: boolean;
  /** Minimum height for mobile screens (sm breakpoint and below) */
  mobileMinHeight?: number | string;
  /** Children should not be provided when using direct props */
  children?: never;
}

// Compound component pattern (existing usage)
interface EmptyStateCompoundProps extends EmptyStateBaseProps {
  /** Child components (EmptyState.Title, EmptyState.Text, custom elements) */
  children: ReactNode;
  /** Show border around the empty state */
  showBorder?: boolean;
  /** Minimum height for mobile screens (sm breakpoint and below) */
  mobileMinHeight?: number | string;
  /** Direct props should not be provided when using compound pattern */
  icon?: never;
  title?: never;
  description?: never;
  size?: never;
}

// Union type for the component
type EmptyStateProps = EmptyStateDirectProps | EmptyStateCompoundProps;

// ========================================
// Sub-Components (Compound Pattern)
// ========================================

function EmptyStateTitle(props: TextProps) {
  return <Text variant="headline-xxl" align="center" {...props} />;
}

function EmptyStateText(props: TextProps) {
  return <Text variant="body-large" align="center" {...props} />;
}

interface EmptyStateIconProps {
  /** Icon size - defaults to medium (48px) */
  size?: EmptyStateSize;
  /** Icon component */
  children: ReactNode;
  /** Additional styles using Mantine's sx prop */
  sx?: Sx | ((theme: MantineTheme) => Sx);
  /** Additional CSS classes */
  className?: string;
}

function EmptyStateIcon({ size = 'md', children, sx, className }: EmptyStateIconProps) {
  const sizeConfig = EMPTY_STATE_SIZES[size];

  return (
    <Box
      className={className}
      mb={sizeConfig.iconMarginBottom}
      sx={(theme) => ({
        color: 'inherit',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(typeof sx === 'function' ? sx(theme) : sx),
      })}
    >
      {isValidElement(children)
        ? cloneElement(children as any, {
            size: (children.props as any)?.size || sizeConfig.iconSize,
          })
        : children}
    </Box>
  );
}

// ========================================
// Implementation Components
// ========================================

const EmptyStateDirectImpl = forwardRef<HTMLDivElement, EmptyStateDirectProps>(
  (
    {
      icon,
      title,
      description,
      backgroundColor,
      borderRadius,
      size = 'md',
      showBorder = false,
      mobileMinHeight,
      px,
      py,
      pt,
      pb,
      pl,
      pr,
      c,
      sx,
      ...stackProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const sizeConfig = EMPTY_STATE_SIZES[size];

    return (
      <Stack
        ref={ref}
        data-test-id={dt.channel.invitesManager.emptyState}
        spacing={sizeConfig.spacing}
        align="center"
        justify="center"
        px={px ?? theme.other.spacing._16}
        py={py ?? theme.other.spacing._48}
        pt={pt}
        pb={pb}
        pl={pl}
        pr={pr}
        c={c ?? 'gray2.3'}
        sx={{
          ...(showBorder && {
            border: `0.5px solid ${theme.colors.gray2[1]}`,
            borderRadius: theme.radius.md,
          }),
          ...(mobileMinHeight && {
            [theme.fn.smallerThan('sm')]: {
              minHeight: mobileMinHeight,
            },
          }),
          backgroundColor,
          borderRadius,
          ...(typeof sx === 'function' ? sx(theme) : sx),
        }}
        {...stackProps}
      >
        {icon && (
          <Box
            mb={sizeConfig.iconMarginBottom}
            sx={{
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isValidElement(icon)
              ? cloneElement(icon as any, {
                  size: (icon.props as any)?.size || sizeConfig.iconSize,
                })
              : icon}
          </Box>
        )}

        {title && <EmptyStateTitle>{title}</EmptyStateTitle>}

        {description && <EmptyStateText>{description}</EmptyStateText>}
      </Stack>
    );
  }
);

EmptyStateDirectImpl.displayName = 'EmptyStateDirectImpl';

const EmptyStateCompoundImpl = forwardRef<HTMLDivElement, EmptyStateCompoundProps>(
  (
    { children, showBorder = false, mobileMinHeight, px, py, pt, pb, pl, pr, c, sx, ...props },
    ref
  ) => {
    const theme = useMantineTheme();
    const defaultSizeConfig = EMPTY_STATE_SIZES.md; // Use same default as direct props

    return (
      <Stack
        ref={ref}
        data-test-id={dt.channel.invitesManager.emptyState}
        spacing={defaultSizeConfig.spacing}
        align="center"
        justify="center"
        px={px ?? theme.other.spacing._16}
        py={py ?? theme.other.spacing._48}
        pt={pt}
        pb={pb}
        pl={pl}
        pr={pr}
        c={c ?? 'gray2.3'}
        sx={{
          ...(showBorder && {
            border: `0.5px solid ${theme.colors.gray2[1]}`,
            borderRadius: theme.radius.md,
          }),
          ...(mobileMinHeight && {
            [theme.fn.smallerThan('sm')]: {
              minHeight: mobileMinHeight,
            },
          }),
          ...(typeof sx === 'function' ? sx(theme) : sx),
        }}
        {...props}
      >
        {children}
      </Stack>
    );
  }
);

EmptyStateCompoundImpl.displayName = 'EmptyStateCompoundImpl';

// ========================================
// Main Component
// ========================================

/**
 * EmptyState component displays a centered empty state with optional icon, title, and description.
 * Supports both compound component pattern (backwards compatible) and direct props pattern.
 * Applies default gray2.3 color by default, but can be customized via c, sx, or className props.
 *
 * @component
 * @example
 * ```tsx
 * // Compound pattern (existing usage - no changes needed)
 * <EmptyState>
 *   <EmptyState.Title>No items found</EmptyState.Title>
 *   <EmptyState.Text>Description here</EmptyState.Text>
 * </EmptyState>
 *
 * // Compound pattern with icon
 * <EmptyState>
 *   <EmptyState.Icon><IconUsers /></EmptyState.Icon>
 *   <EmptyState.Title>No entrants yet</EmptyState.Title>
 *   <EmptyState.Text>Go to Invites Manager to invite people!</EmptyState.Text>
 * </EmptyState>
 *
 * // Compound pattern with custom icon styling
 * <EmptyState>
 *   <EmptyState.Icon sx={{ opacity: 0.7, transform: 'rotate(45deg)' }}>
 *     <IconUsers />
 *   </EmptyState.Icon>
 *   <EmptyState.Title>Styled icon example</EmptyState.Title>
 * </EmptyState>
 *
 * // Direct props pattern (new capability)
 * <EmptyState
 *   icon={<IconUsers />}
 *   title="No items found"
 *   description="Description here"
 *   size="md"
 * />
 *
 * // With border
 * <EmptyState
 *   showBorder
 *   title="Bordered empty state"
 *   description="This has a border and border radius"
 * />
 *
 * // With responsive min height
 * <EmptyState
 *   mih={400}
 *   mobileMinHeight={300}
 *   title="Responsive height"
 *   description="Different min heights for desktop and mobile"
 * />
 *
 * // Custom color
 * <EmptyState c="blue.5" title="Custom colored empty state" />
 * ```
 */
const EmptyStateComponent = forwardRef<HTMLDivElement, EmptyStateProps>((props, ref) => {
  // Type discrimination: check if using direct props pattern
  const isDirectPropsPattern =
    'title' in props || 'description' in props || 'icon' in props || 'size' in props;

  if (isDirectPropsPattern) {
    return <EmptyStateDirectImpl {...(props as EmptyStateDirectProps)} ref={ref} />;
  }
  return <EmptyStateCompoundImpl {...(props as EmptyStateCompoundProps)} ref={ref} />;
});

EmptyStateComponent.displayName = 'EmptyState';

// Create component with attached compound components for backwards compatibility
export const EmptyState = EmptyStateComponent as typeof EmptyStateComponent & {
  Title: typeof EmptyStateTitle;
  Text: typeof EmptyStateText;
  Icon: typeof EmptyStateIcon;
};

EmptyState.Title = EmptyStateTitle;
EmptyState.Text = EmptyStateText;
EmptyState.Icon = EmptyStateIcon;

// ========================================
// Exports
// ========================================

export type { EmptyStateProps, EmptyStateSize };
export { EMPTY_STATE_SIZES };
